import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
} from '@mui/material';
import { Close, Edit, Delete } from '@mui/icons-material';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';

const EMPTY_FORM = {
    f_discount_percentage: '',
    f_discount_amount: '',
    f_effective_from: new Date().toISOString().slice(0, 10),
    f_effective_to: '',
    f_reason: '',
};

const ProductDiscountDialog = ({ open, onClose, product, onProductUpdated }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [discountHistory, setDiscountHistory] = useState([]);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [editingId, setEditingId] = useState(null);

    const loadHistory = useCallback(async () => {
        if (!product?.f_id) return;
        setLoading(true);
        try {
            const res = await businessAPI.getDiscountHistory(product.f_id);
            setDiscountHistory(res.data || []);
        } catch (err) {
            console.error('Failed to load discount history:', err);
        } finally {
            setLoading(false);
        }
    }, [product?.f_id]);

    useEffect(() => {
        if (open && product?.f_id) {
            loadHistory();
            setForm({ ...EMPTY_FORM, f_effective_from: new Date().toISOString().slice(0, 10) });
            setEditingId(null);
            setError('');
            setSuccess('');
        }
    }, [open, product?.f_id, loadHistory]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [open]);

    const formatError = (err) => {
        if (Array.isArray(err.response?.data?.detail)) return err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        if (typeof err.response?.data?.detail === 'string') return err.response.data.detail;
        return err.message || 'An error occurred';
    };

    const handleStartEdit = (log) => {
        setEditingId(log.f_id);
        const pct = parseFloat(log.f_discount_percentage) || 0;
        const amt = parseFloat(log.f_discount_amount) || 0;
        setForm({
            f_discount_percentage: pct > 0 ? pct.toString() : '',
            f_discount_amount: amt > 0 ? amt.toString() : '',
            f_effective_from: log.f_effective_from?.slice(0, 10) || new Date().toISOString().slice(0, 10),
            f_effective_to: log.f_effective_to?.slice(0, 10) || '',
            f_reason: log.f_reason || '',
        });
        setError('');
        setSuccess('');
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM, f_effective_from: new Date().toISOString().slice(0, 10) });
    };

    const handleSave = async () => {
        if (!form.f_discount_percentage && !form.f_discount_amount) {
            setError('Enter a discount percentage or amount.');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const data = {
                f_discount_percentage: form.f_discount_percentage ? parseFloat(form.f_discount_percentage) : 0,
                f_discount_amount: form.f_discount_amount ? parseFloat(form.f_discount_amount) : 0,
                f_effective_from: form.f_effective_from,
                f_effective_to: form.f_effective_to || null,
                f_reason: form.f_reason,
            };
            let res;
            if (editingId) {
                res = await businessAPI.updateDiscountLog(product.f_id, editingId, data);
            } else {
                res = await businessAPI.addDiscountLog(product.f_id, data);
            }
            const ap = res.data.affected_locked_periods;
            let msg = editingId ? 'Discount updated.' : 'Discount added.';
            if (ap?.length > 0) msg += ` ⚠️ Affects ${ap.length} locked period(s).`;
            setSuccess(msg);
            setEditingId(null);
            setForm({ ...EMPTY_FORM, f_effective_from: new Date().toISOString().slice(0, 10) });
            await loadHistory();
            if (onProductUpdated) onProductUpdated();
        } catch (err) {
            setError(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (discountLogId) => {
        if (!window.confirm('Delete this discount entry?')) return;
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await businessAPI.deleteDiscountLog(product.f_id, discountLogId);
            const ap = res.data.affected_locked_periods;
            let msg = 'Discount entry deleted.';
            if (ap?.length > 0) msg += ` ⚠️ Affects ${ap.length} locked period(s).`;
            setSuccess(msg);
            if (editingId === discountLogId) handleCancelEdit();
            await loadHistory();
            if (onProductUpdated) onProductUpdated();
        } catch (err) {
            setError(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleToggle = async (discountLogId) => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await businessAPI.toggleDiscountStatus(product.f_id, discountLogId);
            const ap = res.data.affected_locked_periods;
            let msg = res.data.message || 'Discount status updated.';
            if (ap?.length > 0) msg += ` ⚠️ Affects ${ap.length} locked period(s).`;
            setSuccess(msg);
            await loadHistory();
            if (onProductUpdated) onProductUpdated();
        } catch (err) {
            setError(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    const activeDiscount = discountHistory.find(d => d.f_is_active);

    const formatDiscount = (log) => {
        if (log.f_discount_percentage > 0) return `${log.f_discount_percentage}%`;
        if (log.f_discount_amount > 0) return `₹${log.f_discount_amount}`;
        return '—';
    };

    if (!product) return null;

    return (
        <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
            <DialogTitle sx={APP_DIALOG_TITLE_SX}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h6">% Discount</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>{product.f_name}</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ mt: 0.5, color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

                {/* Active discount summary */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, alignItems: 'center' }}>
                    {activeDiscount
                        ? <>
                            <Chip label="Active" size="small" color="success" />
                            <Typography variant="body2">
                                {formatDiscount(activeDiscount)} off
                                {activeDiscount.f_effective_to && ` · until ${activeDiscount.f_effective_to.slice(0, 10)}`}
                            </Typography>
                          </>
                        : <Typography variant="body2" color="text.disabled">No active discount</Typography>
                    }
                </Box>

                {/* Add / Edit form */}
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        {editingId ? '✏️ Edit Entry' : '+ New Discount'}
                    </Typography>
                    <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth label="Discount" type="number" size="small"
                                value={form.f_discount_percentage}
                                onChange={(e) => setForm({
                                    ...form,
                                    f_discount_percentage: e.target.value,
                                    f_discount_amount: e.target.value ? '' : form.f_discount_amount,
                                })}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                inputProps={{ min: 0, max: 100, inputMode: 'decimal' }}
                                disabled={!!form.f_discount_amount}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth label="or Amount" type="number" size="small"
                                value={form.f_discount_amount}
                                onChange={(e) => setForm({
                                    ...form,
                                    f_discount_amount: e.target.value,
                                    f_discount_percentage: e.target.value ? '' : form.f_discount_percentage,
                                })}
                                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={{ inputMode: 'decimal' }}
                                disabled={!!form.f_discount_percentage}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth label="From" type="date" size="small"
                                value={form.f_effective_from}
                                onChange={(e) => setForm({ ...form, f_effective_from: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                fullWidth label="To (optional)" type="date" size="small"
                                value={form.f_effective_to}
                                onChange={(e) => setForm({ ...form, f_effective_to: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                helperText="blank = no end"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained" size="small"
                                    onClick={handleSave}
                                    disabled={loading || (!form.f_discount_percentage && !form.f_discount_amount)}
                                >
                                    {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                    {editingId ? 'Update' : 'Save'}
                                </Button>
                                {editingId && (
                                    <Button variant="outlined" size="small" onClick={handleCancelEdit}>
                                        Cancel
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>

                {loading && discountHistory.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : discountHistory.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No discounts yet.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Discount</strong></TableCell>
                                    <TableCell><strong>Period</strong></TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {discountHistory.map((log) => (
                                    <TableRow
                                        key={log.f_id}
                                        sx={editingId === log.f_id ? { bgcolor: 'success.50' } : {}}
                                    >
                                        <TableCell>
                                            <strong>{formatDiscount(log)}</strong>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                                            {log.f_effective_from?.slice(5, 10).replace('-', '/')}
                                            {log.f_effective_to ? ` – ${log.f_effective_to.slice(5, 10).replace('-', '/')}` : '+'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleStartEdit(log)}
                                                    disabled={loading}
                                                    color={editingId === log.f_id ? 'success' : 'default'}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(log.f_id)}
                                                    disabled={loading}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProductDiscountDialog;
